import type { RefObject } from 'react';
import { useState, useEffect } from 'react';

/**
 * Хук для свайпа попапа
 * @param ref - ссылка на попап
 * @param isReset - флаг для сброса локальных стейтов в начальное положение
 * @param onSwipe - коллбэк, который выполнится при свайпе попапа
 * @returns {delta} - расстояние в px, проведенное при свайпе
 */

const DEFAULT_THRESHOLD = 50;

interface UseSwipePopupOptions {
  ref: RefObject<HTMLElement>;
  onSwipe: () => void;
}

export const useSwipePopup = ({ ref, onSwipe }: UseSwipePopupOptions) => {
  const [startPosY, setStartPosY] = useState<number>(0);
  const [currentPosY, setCurrentPosY] = useState<number>(0);
  const [delta, setDelta] = useState<number>(0);
  const [isSwiping, setIsSwiping] = useState<boolean>(false);

  const resetPopup = () => {
    setStartPosY(0);
    setCurrentPosY(0);
    setDelta(0);
  };

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      setStartPosY(e.touches[0].clientY);
      setIsSwiping(true);
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isSwiping) return;

      setCurrentPosY(e.touches[0].clientY);
      setDelta(() => {
        if (currentPosY > 0 && currentPosY - startPosY >= 0) {
          return currentPosY - startPosY;
        }
        return 0;
      });
    };

    const handleTouchEnd = () => {
      setIsSwiping(false);
      if (delta >= DEFAULT_THRESHOLD) {
        onSwipe();
      } else {
        setDelta(0);
      }
    };

    const element = ref?.current;

    if (element) {
      element.addEventListener('touchstart', handleTouchStart);
      element.addEventListener('touchmove', handleTouchMove);
      element.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (element) {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
        element.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [ref, onSwipe, startPosY, currentPosY, delta, isSwiping]);

  return { delta, resetPopup };
};
